import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CustomDateRangePicker from './CustomDateRangePicker';
import PeriodCalendar from './DateRange/PeriodCalendar';
import PeriodPills, { PeriodOption } from './DateRange/PeriodPills';
import PeriodPopover from './DateRange/PeriodPopover';
import {
  DEFAULT_PERIODS,
  formatPeriodLabel,
  isCurrentPeriod,
  isNavigable,
  normalizePeriodValue,
  PeriodType,
  periodStart,
  resolveLegacyPeriod,
  resolveSelection,
} from './DateRange/periodModel';
import './DateRange/DateRangePicker.css';
import useOrgCreatedAt from '../Hooks/useOrgCreatedAt';
import { addDays, endOfDay, isSameDay, startOfDay, localOf } from '../utils/localDate';

interface DateRange {
  start_date: string;
  end_date: string | null;
}

interface DateRangeChangePayload extends DateRange {
  period: string;
}

interface DateRangeSelectorProps {
  periods?: PeriodOption[];
  defaultPeriod?: string;
  onChange?: (range: DateRangeChangePayload) => void;
  storageKey?: string;
}

interface StoredSelection {
  period: PeriodType | null;
  customFrom: string;
  customTo: string;
}

const readItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeItem = (key: string, value: string | undefined): void => {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {}
};

const createStorage = (key: string) => {
  const KEYS = {
    PERIOD: `${key}_period`,
    FROM: `${key}_custom_from`,
    TO: `${key}_custom_to`,
  };

  return {
    load: (): StoredSelection => ({
      period: normalizePeriodValue(readItem(KEYS.PERIOD)),
      customFrom: readItem(KEYS.FROM) || '',
      customTo: readItem(KEYS.TO) || '',
    }),
    save: (period: PeriodType, customFrom: string, customTo: string): void => {
      writeItem(KEYS.PERIOD, period);
      writeItem(KEYS.FROM, period === 'custom' ? customFrom : undefined);
      writeItem(KEYS.TO, period === 'custom' ? customTo : undefined);
    },
  };
};

const pad = (value: number): string => String(value).padStart(2, '0');

/** The picker's "YYYY-MM-DDTHH:mm" carries no zone; it is read as local time, like the presets. */
const parseCivil = (value: string, type: 'start' | 'end'): Date | null => {
  const [datePart, timePart] = value.split('T');
  const [year, month, day] = (datePart || '').split('-').map(Number);

  if (!year || !month || !day) return null;

  if (!timePart) {
    return type === 'end'
      ? localOf(year, month - 1, day, 23, 59, 59, 999)
      : localOf(year, month - 1, day);
  }

  const [hours, minutes, seconds] = timePart.split(':').map(Number);
  const defaultSeconds = type === 'end' ? 59 : 0;

  return localOf(
    year,
    month - 1,
    day,
    hours || 0,
    minutes || 0,
    Number.isFinite(seconds) ? seconds : defaultSeconds,
    type === 'end' ? 999 : 0,
  );
};

const formatCivil = (date: Date): string =>
  [
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`,
  ].join('T');

const normalizeCustomPickerValue = (
  value: string,
  type: 'start' | 'end',
  previousValue: string,
): string => {
  if (!value) return '';

  const next = parseCivil(value, type);
  if (!next) return '';

  const previous = previousValue ? parseCivil(previousValue, type) : null;
  const isDateChanged = !previous || !isSameDay(previous, next);

  if (!isDateChanged) return formatCivil(next);

  return formatCivil(type === 'end' ? endOfDay(next) : startOfDay(next));
};

export const getDateRangeByPeriod = (period: string): DateRange | null =>
  resolveLegacyPeriod(period, new Date());

const msUntilNextDay = (now: Date): number => startOfDay(addDays(now, 1)).getTime() - now.getTime();

export default function DateRangeSelector({
  periods = DEFAULT_PERIODS,
  defaultPeriod = 'day',
  onChange,
  storageKey,
}: DateRangeSelectorProps): React.ReactElement {
  const storage = useMemo(() => (storageKey ? createStorage(storageKey) : null), [storageKey]);
  const orgCreatedAt = useOrgCreatedAt();
  const triggerRef = useRef<HTMLButtonElement>(null);

  const [now, setNow] = useState(() => new Date());
  const [stored] = useState(() => storage?.load());

  const [period, setPeriod] = useState<PeriodType>(
    () => stored?.period ?? normalizePeriodValue(defaultPeriod) ?? 'day',
  );
  const [anchor, setAnchor] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState<string>(() => stored?.customFrom ?? '');
  const [customTo, setCustomTo] = useState<string>(() => stored?.customTo ?? '');

  const lastEmitted = useRef<string | null>(null);

  const handleCustomFromChange = (value: string) =>
    setCustomFrom((previous) => normalizeCustomPickerValue(value, 'start', previous));

  const handleCustomToChange = (value: string) =>
    setCustomTo((previous) => normalizeCustomPickerValue(value, 'end', previous));

  const handleSelect = useCallback((next: PeriodType) => {
    setNow(new Date());
    setPeriod(next);
    setAnchor(undefined);
    setOpen(false);
  }, []);

  const handleToggle = useCallback(() => {
    setNow(new Date());
    setOpen((previous) => !previous);
  }, []);

  const handlePick = useCallback(
    (instant: Date) => {
      const pickedAt = new Date();
      setNow(pickedAt);
      if (!isNavigable(period)) setAnchor(instant.toISOString());
      else if (isCurrentPeriod(period, instant, pickedAt)) setAnchor(undefined);
      else setAnchor(periodStart(period, instant).toISOString());
      setOpen(false);
    },
    [period],
  );

  useEffect(() => {
    storage?.save(period, customFrom, customTo);
  }, [storage, period, customFrom, customTo]);

  useEffect(() => {
    if (period === 'custom') return undefined;

    const refresh = () => setNow(new Date());
    const timer = setTimeout(refresh, msUntilNextDay(now) + 1000);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [period, now]);

  const customStart = period === 'custom' && customFrom ? parseCivil(customFrom, 'start') : null;
  const customEnd = period === 'custom' && customTo ? parseCivil(customTo, 'end') : null;
  const isCustomInverted = !!customStart && !!customEnd && customStart > customEnd;

  const range = useMemo((): DateRange | null => {
    if (period === 'custom') {
      if (!customStart || !customEnd || isCustomInverted) return null;
      return { start_date: customStart.toISOString(), end_date: customEnd.toISOString() };
    }

    return resolveSelection({ type: period, anchor }, { min: orgCreatedAt, now });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, anchor, customFrom, customTo, orgCreatedAt, now]);

  useEffect(() => {
    if (!range) return;

    const key = `${period}|${range.start_date}|${range.end_date}`;
    if (key === lastEmitted.current) return;

    lastEmitted.current = key;
    onChange?.({ period, ...range });
  }, [period, range, onChange]);

  const bounds = { min: orgCreatedAt, now };
  const resolvedLabel = formatPeriodLabel({ type: period, anchor }, bounds);

  return (
    <div>
      <PeriodPills
        periods={periods}
        selected={period}
        resolvedLabel={resolvedLabel}
        open={open}
        onSelect={handleSelect}
        onToggle={handleToggle}
        triggerRef={triggerRef}
      >
        {isNavigable(period) && (
          <PeriodPopover
            open={open}
            onClose={() => setOpen(false)}
            anchorRef={triggerRef}
            label={`Select ${period}`}
          >
            <PeriodCalendar
              type={period}
              anchor={anchor ? new Date(anchor) : now}
              bounds={bounds}
              onPick={handlePick}
            />
          </PeriodPopover>
        )}
      </PeriodPills>

      {period === 'custom' && (
        <CustomDateRangePicker
          customFrom={customFrom}
          customTo={customTo}
          setCustomFrom={handleCustomFromChange}
          setCustomTo={handleCustomToChange}
          error={isCustomInverted ? '"From" must be earlier than "To".' : null}
        />
      )}
    </div>
  );
}
